/**
 * Approving and rejecting — the one act on this surface that cannot be taken
 * back.
 *
 * §7.6 records "who decided what and when" and migration 0004 raises if anything
 * tries to change it. There is no undo, no edit, and no second vote: changing
 * your mind is a *new request* that supersedes this one. That is why both
 * decisions go through the same confirmation rather than sitting as two bare
 * buttons in a table — the publish dialog next door sets the standard, and the
 * standard is that a consequential action states its consequence before it is
 * taken, not after.
 *
 * The two consequences differ, so the dialog says different things:
 *
 * - **Approving** may be the last required signature, in which case it clears
 *   the publish gate. The dialog names who else is still required, because "am I
 *   the last one" is the question a reviewer asks and answering it wrongly by
 *   silence is how people approve things they meant to look at again.
 * - **Rejecting** settles the whole request by itself. There is no counting and
 *   no majority in §7.6 — publish unblocks when *all* required reviewers approve,
 *   so one dissent is the outcome — and the draft goes back to its author.
 *
 * The note is short and has no column. It travels in the event metadata because
 * §8.1 draws the line and means it: the approval trail records who approved, the
 * comment trail records what was raised. Anything worth arguing over belongs in
 * an anchored comment, which survives this request being superseded and points
 * at the clause it is about.
 */

import { useState } from 'react';
import { useApprovalDecision } from '../../api/queries.ts';
import type { ApprovalRequest, ApprovalReviewer, ReviewerDecision } from '../../api/types.ts';
import { formatDateTime } from '../../components/format.ts';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState } from '../../components/ui/feedback.tsx';
import { TextField } from '../../components/ui/field.tsx';
import { Mono } from '../../components/ui/layout.tsx';
import { requirementMeaning } from './vocabulary.tsx';

/** `noteSchema` in `approvals/routes.ts`, mirrored so a typo is not a 400. */
const NOTE_LIMIT = 1000;

function reviewerName(reviewer: ApprovalReviewer): string {
  return reviewer.name ?? reviewer.personId;
}

function DecisionDialog({
  request,
  decision,
  self,
  isOpen,
  onOpenChange,
}: {
  request: ApprovalRequest;
  decision: ReviewerDecision;
  self: ApprovalReviewer;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog
      title={decision === 'approved' ? 'Approve this revision' : 'Reject this revision'}
      description="§7.6 records who decided what and when, and does not let it be rewritten."
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {(close) => (
        <DecisionForm request={request} decision={decision} self={self} onClose={close} />
      )}
    </Dialog>
  );
}

function DecisionForm({
  request,
  decision,
  self,
  onClose,
}: {
  request: ApprovalRequest;
  decision: ReviewerDecision;
  self: ApprovalReviewer;
  onClose: () => void;
}) {
  const decide = useApprovalDecision(request.id);
  const [note, setNote] = useState('');

  // Everyone required who has not answered, minus the person about to answer.
  // This is what turns "approve" from a shrug into a decision: it is the
  // difference between adding a signature and being the last one.
  const stillRequired = request.reviewers.filter(
    (reviewer) =>
      reviewer.requirement === 'required' &&
      reviewer.decision === null &&
      reviewer.personId !== self.personId,
  );
  const lastRequired = self.requirement === 'required' && stillRequired.length === 0;
  const tooLong = note.trim().length > NOTE_LIMIT;

  return (
    <>
      <DialogBody>
        {decision === 'approved' ? (
          <Alert
            tone={lastRequired ? 'warning' : 'info'}
            title={consequenceTitle(self, lastRequired)}
          >
            <div className="flex flex-col gap-2">
              <p>{requirementMeaning(self.requirement)}</p>
              {lastRequired ? (
                <p>
                  You are the last required reviewer who has not answered, so approving clears the
                  publish gate. Publishing is still a separate act — §7.4 has approval promote the
                  draft only when somebody publishes it — but nothing else stands in the way
                  afterwards.
                </p>
              ) : (
                <p>
                  Still required after you:{' '}
                  <span className="font-semibold text-primary">
                    {stillRequired.map(reviewerName).join(', ')}
                  </span>
                  . Publishing waits on them regardless of what you do here.
                </p>
              )}
            </div>
          </Alert>
        ) : (
          <Alert tone="error" title="Rejecting settles this request on its own">
            <div className="flex flex-col gap-2">
              <p>
                There is no counting and no majority: §7.6 unblocks publishing only when
                <em> every </em>required reviewer approves, so one rejection is the whole outcome
                even with {stillRequired.length} other required{' '}
                {stillRequired.length === 1 ? 'reviewer' : 'reviewers'} still to answer.
              </p>
              <p>
                The draft goes back to its author. Your rejection cannot be reversed or removed —
                the way past it is a new request that supersedes this one, once the objection has
                been addressed.
              </p>
            </div>
          </Alert>
        )}

        <TextField
          label="A short note, if it helps"
          value={note}
          onChange={setNote}
          multiline
          rows={3}
          description="Recorded with the decision in the activity log. It is not a comment thread — anything worth arguing over belongs in an anchored comment on the clause it is about (§8.1)."
          {...(tooLong ? { errorMessage: `Keep it under ${NOTE_LIMIT} characters.` } : {})}
        />

        {decide.isError ? <ErrorState error={decide.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant={decision === 'approved' ? 'primary' : 'destructive'}
          isPending={decide.isPending}
          isDisabled={tooLong}
          onPress={() =>
            decide.mutate(
              { decision, ...(note.trim() ? { note: note.trim() } : {}) },
              { onSuccess: onClose },
            )
          }
        >
          {decision === 'approved' ? 'Record my approval' : 'Record my rejection'}
        </Button>
      </DialogFooter>
    </>
  );
}

function consequenceTitle(self: ApprovalReviewer, lastRequired: boolean): string {
  if (self.requirement === 'advisory') return 'Your approval is advisory';
  return lastRequired ? 'This clears the publish gate' : 'One of several required approvals';
}

/**
 * The reviewer's own row, lifted out of the table and given the weight of an
 * action.
 *
 * Four states, and each of them is a different sentence rather than a hidden
 * button. The one that is easy to get wrong is the last: somebody looking at a
 * request they are not on should be told that, because §7.6 makes reviewers an
 * explicit set and "nobody asked you" is a fact about the request rather than a
 * failure of the page to load its buttons.
 */
export function ReviewerDecisionPanel({
  request,
  viewerId,
}: {
  request: ApprovalRequest;
  viewerId: string | undefined;
}) {
  const [pending, setPending] = useState<ReviewerDecision | null>(null);
  const self = request.reviewers.find((reviewer) => reviewer.personId === viewerId);

  if (!self) {
    return request.open ? (
      <Alert tone="info" title="You are not a reviewer on this request">
        §7.6 makes reviewers an explicit set — a decision from somebody nobody asked is a vote that
        was never requested. Whoever opened this can add you.
      </Alert>
    ) : null;
  }

  if (self.decision !== null) {
    return (
      <Alert
        tone={self.decision === 'approved' ? 'success' : 'error'}
        title={`You ${self.decision} this revision on ${formatDateTime(self.decidedAt)}`}
      >
        That record is immutable (§7.6). If your answer has changed, the way to say so is a new
        approval request on the current draft — not a rewritten row.
      </Alert>
    );
  }

  if (!request.open) {
    return (
      <Alert tone="info" title="This request is closed and you never answered">
        Nothing here edits a closed request. An advisory point that arrives after the decision
        belongs in a comment (§8.1), which stays readable long after the request stops being.
      </Alert>
    );
  }

  return (
    <>
      <Alert
        tone={self.requirement === 'required' ? 'warning' : 'info'}
        title={
          self.requirement === 'required'
            ? 'Publishing is waiting on you'
            : 'Your opinion was asked for, and it is not a gate'
        }
      >
        <div className="flex flex-col gap-3">
          <p>{requirementMeaning(self.requirement)}</p>
          <p className="text-xs opacity-80">
            You were added on {formatDateTime(self.addedAt)} as <Mono>{self.requirement}</Mono>.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onPress={() => setPending('approved')}>
              Approve
            </Button>
            <Button variant="destructive-secondary" onPress={() => setPending('rejected')}>
              Reject
            </Button>
          </div>
        </div>
      </Alert>

      {pending ? (
        <DecisionDialog
          request={request}
          decision={pending}
          self={self}
          isOpen
          onOpenChange={(open) => {
            if (!open) setPending(null);
          }}
        />
      ) : null}
    </>
  );
}
